/**
 * Currencies: schemas, list search, queries and mutations (GET/POST/PATCH/DELETE /api/currencies).
 * Traps handled here (rgs-api-contract): POST with an existing code overwrites that row, so create
 * checks first; POST defaults IsValuable to 0, so it's always sent; PATCH sends changed fields only.
 */
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { ApiError, isApiError } from '@/api/errors';
import { http } from '@/api/http';
import {
  encodePagedQuery,
  pagedSchema,
  type FilterDescriptor,
  type PagedQuery,
  type SortDescriptor,
} from '@/api/paged-query';
import { decimalString, flag } from '@/api/zod-helpers';
import type { FieldMeta } from '@/lib/diff';
import { filterParam, listSearchSchema, toPagedQuery } from '@/lib/url-state';

export const CURRENCY_TYPES = ['fiat', 'crypto', 'social'] as const;
export type CurrencyType = (typeof CURRENCY_TYPES)[number];

export const CURRENCY_TYPE_LABELS: Record<CurrencyType, string> = {
  fiat: 'Fiat',
  crypto: 'Crypto',
  social: 'Social coin',
};

export const typeLabel = (type: string) => CURRENCY_TYPE_LABELS[type as CurrencyType] ?? type;

export const currencySchema = z.object({
  Currency: z.string().min(1),
  /** Units of this currency per 1 EUR; analytics convert with it. */
  ExchangeRate: decimalString,
  /** Units per 1 EUR used to build bet ladders; often a rounded exchange rate. */
  BetConfigExchangeRate: decimalString,
  Status: flag,
  /** false = social coin: kept out of money analytics. */
  IsValuable: flag,
  CreditValuesDivisor: z.coerce.number().int(),
  Type: z.string(),
  UpdatedAt: z
    .string()
    .nullish()
    .transform((v) => v ?? null),
});

export type Currency = z.infer<typeof currencySchema>;

/** A social coin marked valuable counts in money analytics (GGR, RTP): almost always a data error. */
export const isValuableSocialCoin = (c: Pick<Currency, 'Type' | 'IsValuable'>) => c.Type === 'social' && c.IsValuable;

// ---- list -------------------------------------------------------------------------------------

/** The server's sortable allow-list (module-fields.config.ts, `currencies`). */
export const CURRENCY_SORT_FIELDS = [
  'Currency',
  'ExchangeRate',
  'BetConfigExchangeRate',
  'Status',
  'CreditValuesDivisor',
  'Type',
  'IsValuable',
  'UpdatedAt',
] as const;

export const DEFAULT_CURRENCY_SORT: SortDescriptor = { field: 'Currency', dir: 'asc' };

export const currenciesSearchSchema = listSearchSchema({
  sortFields: CURRENCY_SORT_FIELDS,
  filters: {
    type: filterParam.enum(CURRENCY_TYPES),
    valuable: filterParam.boolean(),
    active: filterParam.boolean(),
  },
});

export type CurrenciesSearch = z.infer<typeof currenciesSearchSchema>;

export function currenciesPagedQuery(search: CurrenciesSearch): PagedQuery {
  const filters: FilterDescriptor[] = [];
  if (search.q) filters.push({ field: 'Currency', operator: 'contains', value: search.q });
  if (search.type) filters.push({ field: 'Type', operator: 'eq', value: search.type });
  if (search.valuable !== undefined)
    filters.push({ field: 'IsValuable', operator: 'eq', value: search.valuable ? 1 : 0 });
  if (search.active !== undefined) filters.push({ field: 'Status', operator: 'eq', value: search.active ? 1 : 0 });
  return toPagedQuery(search, { defaultSort: DEFAULT_CURRENCY_SORT, filters });
}

const listSchema = pagedSchema(currencySchema);

export const currenciesKeys = {
  all: ['currencies'] as const,
  list: (query: PagedQuery) => ['currencies', 'list', query] as const,
  detail: (code: string) => ['currencies', 'detail', code] as const,
};

export const currenciesListQuery = (search: CurrenciesSearch) => {
  const query = currenciesPagedQuery(search);
  return queryOptions({
    queryKey: currenciesKeys.list(query),
    queryFn: ({ signal }) => http('/currencies', { query: encodePagedQuery(query), signal }, listSchema),
    placeholderData: keepPreviousData,
  });
};

export const currencyQuery = (code: string) =>
  queryOptions({
    queryKey: currenciesKeys.detail(code),
    queryFn: ({ signal }) => http(`/currencies/${encodeURIComponent(code)}`, { signal }, currencySchema),
  });

// ---- writes -----------------------------------------------------------------------------------

/** What the form edits. Rates stay exact decimal strings; flags are booleans until sent. */
export interface CurrencyValues extends Record<string, unknown> {
  ExchangeRate: string;
  BetConfigExchangeRate: string;
  CreditValuesDivisor: number;
  Type: CurrencyType;
  IsValuable: boolean;
  Status: boolean;
}

export const CURRENCY_FIELDS: FieldMeta[] = [
  { key: 'ExchangeRate', label: 'Exchange rate (per EUR)' },
  { key: 'BetConfigExchangeRate', label: 'Bet-config rate (per EUR)' },
  { key: 'CreditValuesDivisor', label: 'Credit values divisor' },
  { key: 'Type', label: 'Type', format: (v) => typeLabel(String(v)) },
  { key: 'IsValuable', label: 'Valuable' },
  { key: 'Status', label: 'Status', format: (v) => (v ? 'Active' : 'Inactive') },
];

export const toCurrencyValues = (c: Currency): CurrencyValues => ({
  ExchangeRate: c.ExchangeRate,
  BetConfigExchangeRate: c.BetConfigExchangeRate,
  CreditValuesDivisor: c.CreditValuesDivisor,
  Type: (CURRENCY_TYPES as readonly string[]).includes(c.Type) ? (c.Type as CurrencyType) : 'fiat',
  IsValuable: c.IsValuable,
  Status: c.Status,
});

/** The API stores flags as 0/1 tinyints. */
export function toWireBody(values: Partial<CurrencyValues> & { Currency?: string }): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(values)) {
    body[key] = key === 'IsValuable' || key === 'Status' ? (value ? 1 : 0) : value;
  }
  return body;
}

export async function currencyExists(code: string): Promise<boolean> {
  try {
    await http(`/currencies/${encodeURIComponent(code)}`, {}, currencySchema);
    return true;
  } catch (error) {
    if (isApiError(error) && error.code === 'not_found') return false;
    throw error;
  }
}

export function useUpdateCurrency(code: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: Record<string, unknown>) =>
      http(`/currencies/${encodeURIComponent(code)}`, { method: 'PATCH', body: toWireBody(patch) }, currencySchema),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: currenciesKeys.all });
    },
  });
}

export function useCreateCurrency() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (values: CurrencyValues & { Currency: string }) => {
      if (await currencyExists(values.Currency)) {
        throw new ApiError({
          status: 409,
          code: 'conflict',
          messages: [`${values.Currency} already exists. Creating it again would overwrite it, so edit it instead.`],
        });
      }
      return http('/currencies', { method: 'POST', body: toWireBody(values) }, currencySchema);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: currenciesKeys.all });
    },
  });
}

export function useDeleteCurrency(code: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => http(`/currencies/${encodeURIComponent(code)}`, { method: 'DELETE' }),
    onSuccess: async () => {
      queryClient.removeQueries({ queryKey: currenciesKeys.detail(code) });
      await queryClient.invalidateQueries({ queryKey: currenciesKeys.all });
    },
  });
}
